import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
    ClipboardList, FileText, Users, Briefcase, ShieldCheck, CalendarDays,
    ChevronRight, Plus, Pencil, ArrowRight, AlertTriangle,
} from 'lucide-react';

import { useApi } from '../../lib/api.js';
import { useCurrentUser } from '../../hooks/useCurrentUser.js';
import { PageHeader, LoadingPage, StatusPill } from '../../components/ui/Feedback.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { Input, Select } from '../../components/ui/Form.jsx';

// =============================================================================
// ReportsPage — one place for everything you generate, print or send
// =============================================================================
// Left: grouped list of reports. Right: the selected report.
// Selected report lives in the URL (?r=…) so it can be linked/bookmarked.
// =============================================================================

const GROUPS = [
    {
        label: 'Statements',
        items: [
            { key: 'sales-exec', label: 'Sales exec statements', icon: Briefcase },
            { key: 'chauffeur', label: 'Chauffeur statements', icon: Users },
        ],
    },
    {
        label: 'Bookings',
        items: [
            { key: 'bookings', label: 'Bookings list / print', icon: CalendarDays },
        ],
    },
    {
        label: 'Compliance',
        items: [
            { key: 'tfl', label: 'TfL reports', icon: ShieldCheck },
        ],
    },
];

export function ReportsPage() {
    const { isOwner, isLoading } = useCurrentUser();
    const [params, setParams] = useSearchParams();
    const active = params.get('r') || 'sales-exec';

    if (isLoading) return <LoadingPage />;
    if (!isOwner) {
        return <div className="p-6 text-sm text-ink-subtle">Owner access required.</div>;
    }

    return (
        <div>
            <PageHeader
                title={
                    <span className="inline-flex items-center gap-3">
                        <ClipboardList className="h-6 w-6 text-ink-subtle" />
                        <span>Reports</span>
                    </span>
                }
                subtitle="Statements, reports and printouts — generate everything from here."
            />

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
                <nav className="lg:col-span-1">
                    <div className="card">
                        <div className="card-body space-y-4">
                            {GROUPS.map((g) => (
                                <div key={g.label}>
                                    <div className="text-2xs uppercase tracking-wider font-semibold text-ink-subtle mb-1.5 px-2">
                                        {g.label}
                                    </div>
                                    <ul className="space-y-0.5">
                                        {g.items.map((it) => {
                                            const Icon = it.icon;
                                            const selected = active === it.key;
                                            return (
                                                <li key={it.key}>
                                                    <button
                                                        type="button"
                                                        onClick={() => setParams({ r: it.key })}
                                                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left transition-colors ${
                                                            selected ? 'bg-ink text-paper' : 'text-ink-muted hover:bg-paper-hover hover:text-ink'
                                                        }`}
                                                    >
                                                        <Icon className="h-4 w-4 flex-shrink-0" />
                                                        {it.label}
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </nav>

                <div className="lg:col-span-3 space-y-5">
                    {active === 'sales-exec' && <SalesExecReport />}
                    {active === 'chauffeur' && <ChauffeurStatementsReport />}
                    {active === 'bookings' && <LinkOutReport
                        title="Bookings list / print"
                        text="Filter the bookings list by client, chauffeur or dates, then use Print to save or print it. Tick Client copy to hide internal details."
                        to="/bookings"
                        button="Open bookings list"
                    />}
                    {active === 'tfl' && <LinkOutReport
                        title="TfL reports"
                        text="Weekly driver report, weekly vehicle report and the bookings audit report for TfL or other licensing authorities."
                        to="/compliance"
                        button="Open compliance reports"
                    />}
                </div>
            </div>
        </div>
    );
}

// =============================================================================
// Sales exec statements
// =============================================================================
function SalesExecReport() {
    const api = useApi();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const execsQuery = useQuery({
        queryKey: ['sales-execs'],
        queryFn: () => api.get('/sales-execs'),
    });
    const execs = execsQuery.data?.sales_execs || [];
    const activeExecs = execs.filter((e) => e.status === 'active');

    const [execId, setExecId] = useState('');
    const lastMonth = useMemo(() => presetRange('last_month'), []);
    const [periodStart, setPeriodStart] = useState(lastMonth.start);
    const [periodEnd, setPeriodEnd] = useState(lastMonth.end);
    const [managing, setManaging] = useState(false);

    const selectedExecId = execId || activeExecs[0]?.id || '';

    const previewQuery = useQuery({
        queryKey: ['sales-exec-preview', selectedExecId, periodStart, periodEnd],
        queryFn: () => api.get(`/sales-execs/${selectedExecId}/commission-preview`, {
            query: { period_start: periodStart, period_end: periodEnd },
        }),
        enabled: false,
    });

    const statementsQuery = useQuery({
        queryKey: ['sales-exec-statements'],
        queryFn: () => api.get('/sales-exec-statements'),
    });

    const generateMutation = useMutation({
        mutationFn: () => api.post(`/sales-execs/${selectedExecId}/statements`, {
            period_start: periodStart,
            period_end: periodEnd,
        }),
        onSuccess: (r) => {
            toast.success(`Statement #${r.statement.statement_number} generated`);
            queryClient.invalidateQueries({ queryKey: ['sales-exec-statements'] });
            navigate(`/reports/sales-exec-statements/${r.statement.id}`);
        },
        onError: (err) => toast.error(err.message || 'Could not generate statement'),
    });

    const preview = previewQuery.data;
    const previewMatches = preview
        && preview.sales_exec?.id === selectedExecId
        && preview.period_start === periodStart
        && preview.period_end === periodEnd;

    function applyPreset(key) {
        const r = presetRange(key);
        setPeriodStart(r.start);
        setPeriodEnd(r.end);
    }

    return (
        <>
            <div className="card">
                <div className="card-header flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <h2 className="text-sm font-semibold text-ink">Sales exec statements</h2>
                        <p className="text-xs text-ink-subtle mt-0.5">
                            Commission on invoiced bookings for the exec’s clients — fare lines ex VAT, expenses excluded.
                        </p>
                    </div>
                    <Button variant="secondary" size="sm" onClick={() => setManaging(true)}>
                        <Users className="h-3.5 w-3.5" />
                        Manage sales execs
                    </Button>
                </div>

                <div className="card-body space-y-4">
                    {execsQuery.isSuccess && activeExecs.length === 0 ? (
                        <div className="text-sm text-ink-subtle">
                            No sales execs yet. Click <strong>Manage sales execs</strong> to add one, then link them to
                            clients from each customer’s page.
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <Select
                                    label="Sales exec"
                                    value={selectedExecId}
                                    onChange={(e) => setExecId(e.target.value)}
                                >
                                    {activeExecs.map((e) => (
                                        <option key={e.id} value={e.id}>{e.name}</option>
                                    ))}
                                </Select>
                                <Input label="From" type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
                                <Input label="To" type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
                            </div>
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    {[
                                        ['last_month', 'Last month'],
                                        ['this_month', 'This month'],
                                        ['last_week', 'Last week'],
                                        ['this_year', 'This year'],
                                    ].map(([k, l]) => (
                                        <button
                                            key={k}
                                            type="button"
                                            onClick={() => applyPreset(k)}
                                            className="px-2 py-1 rounded text-xs border border-paper-border text-ink-subtle hover:text-ink hover:border-ink"
                                        >
                                            {l}
                                        </button>
                                    ))}
                                </div>
                                <Button
                                    variant="primary"
                                    onClick={() => previewQuery.refetch()}
                                    loading={previewQuery.isFetching}
                                    disabled={!selectedExecId || !periodStart || !periodEnd || periodStart > periodEnd}
                                >
                                    Preview
                                </Button>
                            </div>
                        </>
                    )}
                </div>

                {previewQuery.isError && (
                    <div className="card-body border-t border-paper-border-soft text-sm text-status-danger">
                        {previewQuery.error?.message || 'Could not load preview'}
                    </div>
                )}

                {previewMatches && (
                    <div className="border-t border-paper-border-soft">
                        {preview.earlier_unstatemented_count > 0 && (
                            <div className="mx-6 mt-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                                <span>
                                    {preview.earlier_unstatemented_count} invoiced booking
                                    {preview.earlier_unstatemented_count === 1 ? '' : 's'} before this period
                                    {preview.earlier_unstatemented_count === 1 ? ' is' : ' are'} not on any statement yet.
                                    Widen the From date to include {preview.earlier_unstatemented_count === 1 ? 'it' : 'them'}.
                                </span>
                            </div>
                        )}

                        {preview.lines.length === 0 ? (
                            <p className="text-sm text-ink-subtle text-center py-6">
                                No invoiced bookings for {preview.sales_exec.name}’s clients in this period.
                            </p>
                        ) : (
                            <>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-paper-border-soft text-xs text-ink-subtle uppercase tracking-wider">
                                                <th className="text-left px-6 py-2 font-medium">Date</th>
                                                <th className="text-left px-3 py-2 font-medium">Ref</th>
                                                <th className="text-left px-3 py-2 font-medium">Client</th>
                                                <th className="text-right px-3 py-2 font-medium">Fare ex VAT</th>
                                                <th className="text-right px-3 py-2 font-medium">Rate</th>
                                                <th className="text-right px-6 py-2 font-medium">Commission</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {preview.lines.map((l) => (
                                                <tr key={l.booking_id} className="border-b border-paper-border-soft last:border-b-0">
                                                    <td className="px-6 py-2">{fmtDate(l.pickup_date)}</td>
                                                    <td className="px-3 py-2">
                                                        <Link to={`/bookings/${l.booking_id}`} className="font-mono text-xs hover:underline">
                                                            {l.ride_ref}
                                                        </Link>
                                                        {['cancelled', 'no_show'].includes(l.booking_status) && (
                                                            <span className="ml-1.5 text-2xs uppercase text-ink-subtle">
                                                                {l.booking_status === 'no_show' ? 'No-show' : 'Cancelled'}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2 text-ink-muted">{l.customer_name}</td>
                                                    <td className="px-3 py-2 text-right tabular-nums">{pence(l.fare_ex_vat_pence)}</td>
                                                    <td className="px-3 py-2 text-right tabular-nums">{Number(l.commission_pct)}%</td>
                                                    <td className="px-6 py-2 text-right tabular-nums font-medium">{pence(l.commission_pence)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="px-6 py-4 border-t border-paper-border-soft flex items-center justify-between gap-4 flex-wrap">
                                    <div className="text-sm text-ink-subtle">
                                        {preview.lines.length} booking{preview.lines.length === 1 ? '' : 's'} · fares {pence(preview.base_total_pence)}
                                        <span className="ml-3 text-ink font-semibold">Commission {pence(preview.commission_total_pence)}</span>
                                    </div>
                                    <Button
                                        variant="primary"
                                        onClick={() => generateMutation.mutate()}
                                        loading={generateMutation.isPending}
                                    >
                                        <FileText className="h-4 w-4" />
                                        Generate statement
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            <StatementsList statementsQuery={statementsQuery} />

            {managing && <ManageExecsModal execs={execs} onClose={() => setManaging(false)} />}
        </>
    );
}

function StatementsList({ statementsQuery }) {
    const statements = statementsQuery.data?.statements || [];
    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">Issued sales exec statements</h2>
                <p className="text-xs text-ink-subtle mt-0.5">Newest first.</p>
            </div>
            {statementsQuery.isLoading ? (
                <div className="card-body text-sm text-ink-subtle">Loading…</div>
            ) : statements.length === 0 ? (
                <div className="card-body text-sm text-ink-subtle">No statements issued yet.</div>
            ) : (
                <div className="divide-y divide-paper-border-soft">
                    {statements.map((s) => (
                        <Link
                            key={s.id}
                            to={`/reports/sales-exec-statements/${s.id}`}
                            className="flex items-center gap-4 px-6 py-3 hover:bg-paper-hover/50 transition-colors"
                        >
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-semibold text-ink">#{s.statement_number}</span>
                                    <span className="text-sm text-ink-muted">{s.sales_exec_name}</span>
                                    <StatementPill status={s.status} />
                                </div>
                                <div className="text-xs text-ink-subtle mt-0.5">
                                    {fmtDate(s.period_start)} – {fmtDate(s.period_end)} · {s.line_count} booking{s.line_count === 1 ? '' : 's'}
                                    {' · '}issued {fmtDate(s.issued_at)}
                                </div>
                            </div>
                            <div className="text-sm font-semibold text-ink tabular-nums">{pence(s.commission_total_pence)}</div>
                            <ChevronRight className="h-4 w-4 text-ink-faint" />
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}

// -----------------------------------------------------------------------------
// Manage sales execs — list + add + edit
// -----------------------------------------------------------------------------
function ManageExecsModal({ execs, onClose }) {
    const [editing, setEditing] = useState(null);   // exec object or 'new'

    if (editing) {
        return (
            <ExecFormModal
                exec={editing === 'new' ? null : editing}
                onClose={() => setEditing(null)}
            />
        );
    }

    return (
        <Modal open onClose={onClose} title="Sales execs" size="md">
            <div className="space-y-3">
                {execs.length === 0 && (
                    <p className="text-sm text-ink-subtle">No sales execs yet.</p>
                )}
                <ul className="divide-y divide-paper-border-soft border border-paper-border-soft rounded-md">
                    {execs.map((e) => (
                        <li key={e.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                            <div className="min-w-0">
                                <div className="text-sm font-medium text-ink flex items-center gap-2">
                                    {e.name}
                                    {e.status !== 'active' && (
                                        <span className="text-2xs uppercase tracking-wider text-ink-subtle">Inactive</span>
                                    )}
                                </div>
                                <div className="text-xs text-ink-subtle truncate">
                                    {Number(e.default_commission_pct)}% default · {e.active_client_count} client{e.active_client_count === 1 ? '' : 's'}
                                    {e.email ? ` · ${e.email}` : ''}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setEditing(e)}
                                className="p-1.5 rounded text-ink-subtle hover:text-ink hover:bg-paper-hover"
                                aria-label="Edit"
                            >
                                <Pencil className="h-3.5 w-3.5" />
                            </button>
                        </li>
                    ))}
                </ul>
                <div className="flex items-center justify-between pt-4 border-t border-paper-border-soft">
                    <Button variant="secondary" onClick={onClose}>Close</Button>
                    <Button variant="primary" onClick={() => setEditing('new')}>
                        <Plus className="h-4 w-4" />
                        Add sales exec
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

function ExecFormModal({ exec, onClose }) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [name, setName] = useState(exec?.name || '');
    const [email, setEmail] = useState(exec?.email || '');
    const [phone, setPhone] = useState(exec?.phone || '');
    const [pctValue, setPctValue] = useState(exec ? String(Number(exec.default_commission_pct)) : '10');
    const [active, setActive] = useState(exec ? exec.status === 'active' : true);

    const pctNum = Number(pctValue);
    const valid = name.trim() && Number.isFinite(pctNum) && pctNum >= 0 && pctNum <= 100;

    const mutation = useMutation({
        mutationFn: () => {
            const body = {
                name: name.trim(),
                email: email.trim() || null,
                phone: phone.trim() || null,
                default_commission_pct: pctNum,
            };
            if (exec) return api.patch(`/sales-execs/${exec.id}`, { ...body, status: active ? 'active' : 'inactive' });
            return api.post('/sales-execs', body);
        },
        onSuccess: () => {
            toast.success(exec ? 'Sales exec updated' : 'Sales exec added');
            queryClient.invalidateQueries({ queryKey: ['sales-execs'] });
            onClose();
        },
        onError: (err) => toast.error(err.message || 'Could not save'),
    });

    return (
        <Modal open onClose={onClose} title={exec ? 'Edit sales exec' : 'Add sales exec'} size="md">
            <div className="space-y-3">
                <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
                <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} hint="Statements are emailed here" />
                <Input label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
                <Input
                    label="Default commission %"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={pctValue}
                    onChange={(e) => setPctValue(e.target.value)}
                    hint="Used for every client unless the client link sets its own %."
                />
                {exec && (
                    <label className="flex items-center gap-2 text-sm text-ink">
                        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
                        Active
                    </label>
                )}
                <div className="flex items-center justify-end gap-2 pt-4 border-t border-paper-border-soft">
                    <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
                    <Button variant="primary" onClick={() => mutation.mutate()} loading={mutation.isPending} disabled={!valid}>
                        Save
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

// =============================================================================
// Chauffeur statements — overview, links to the existing pages
// =============================================================================
function ChauffeurStatementsReport() {
    const api = useApi();
    const navigate = useNavigate();
    const [driverId, setDriverId] = useState('');

    const poolQuery = useQuery({
        queryKey: ['statements', 'pool-pending'],
        queryFn: () => api.get('/statements/pool/pending-by-chauffeur'),
    });
    const unpaidQuery = useQuery({
        queryKey: ['statements', 'unpaid'],
        queryFn: () => api.get('/statements/unpaid'),
    });
    const driversQuery = useQuery({
        queryKey: ['drivers', { status: 'active' }],
        queryFn: () => api.get('/drivers', { query: { status: 'active' } }),
    });

    const pool = poolQuery.data?.chauffeurs || [];
    const unpaid = unpaidQuery.data?.statements || [];
    const drivers = (driversQuery.data?.drivers || [])
        .slice()
        .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));

    return (
        <>
            <div className="card">
                <div className="card-header">
                    <h2 className="text-sm font-semibold text-ink">Chauffeur statements</h2>
                    <p className="text-xs text-ink-subtle mt-0.5">
                        Pick a chauffeur to prepare and generate their statement.
                    </p>
                </div>
                <div className="card-body flex items-end gap-3 flex-wrap">
                    <div className="flex-1 min-w-[220px]">
                        <Select label="Chauffeur / operator" value={driverId} onChange={(e) => setDriverId(e.target.value)}>
                            <option value="">Select…</option>
                            {drivers.map((d) => (
                                <option key={d.id} value={d.id}>
                                    {d.first_name} {d.last_name}{d.call_sign ? ` (${d.call_sign})` : ''}
                                </option>
                            ))}
                        </Select>
                    </div>
                    <Button
                        variant="primary"
                        disabled={!driverId}
                        onClick={() => navigate(`/chauffeurs/${driverId}/statements`)}
                    >
                        Open statements
                        <ArrowRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            <div className="card">
                <div className="card-header">
                    <h2 className="text-sm font-semibold text-ink">Ready to statement</h2>
                    <p className="text-xs text-ink-subtle mt-0.5">Chauffeurs with locked lines waiting in their pool.</p>
                </div>
                {pool.length === 0 ? (
                    <div className="card-body text-sm text-ink-subtle">Nothing waiting.</div>
                ) : (
                    <div className="divide-y divide-paper-border-soft">
                        {pool.map((c) => (
                            <Link
                                key={c.chauffeur_id}
                                to={`/chauffeurs/${c.chauffeur_id}/statements`}
                                className="flex items-center gap-4 px-6 py-3 hover:bg-paper-hover/50"
                            >
                                <div className="flex-1 text-sm text-ink">
                                    {c.chauffeur_first_name} {c.chauffeur_last_name}
                                    <span className="text-xs text-ink-subtle ml-2">{c.line_count} line{c.line_count === 1 ? '' : 's'}</span>
                                </div>
                                <div className="text-sm font-medium tabular-nums">{pence(c.net_total_pence)}</div>
                                <ChevronRight className="h-4 w-4 text-ink-faint" />
                            </Link>
                        ))}
                    </div>
                )}
            </div>

            <div className="card">
                <div className="card-header">
                    <h2 className="text-sm font-semibold text-ink">Sent, not yet paid</h2>
                </div>
                {unpaid.length === 0 ? (
                    <div className="card-body text-sm text-ink-subtle">All chauffeur statements are paid.</div>
                ) : (
                    <div className="divide-y divide-paper-border-soft">
                        {unpaid.map((s) => (
                            <Link
                                key={s.id}
                                to={`/statements/${s.id}`}
                                className="flex items-center gap-4 px-6 py-3 hover:bg-paper-hover/50"
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-ink">
                                        #{s.statement_number} · {s.chauffeur_first_name} {s.chauffeur_last_name}
                                    </div>
                                    <div className="text-xs text-ink-subtle">
                                        {fmtDate(s.period_start)} – {fmtDate(s.period_end)} · issued {fmtDate(s.issued_at)}
                                    </div>
                                </div>
                                <div className="text-sm font-medium tabular-nums">{pence(s.grand_total_pence)}</div>
                                <ChevronRight className="h-4 w-4 text-ink-faint" />
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}

function LinkOutReport({ title, text, to, button }) {
    return (
        <div className="card">
            <div className="card-header">
                <h2 className="text-sm font-semibold text-ink">{title}</h2>
            </div>
            <div className="card-body space-y-3">
                <p className="text-sm text-ink-muted">{text}</p>
                <p className="text-xs text-ink-subtle">This report moves fully into Reports in the next update.</p>
                <Link to={to}>
                    <Button variant="primary">
                        {button}
                        <ArrowRight className="h-4 w-4" />
                    </Button>
                </Link>
            </div>
        </div>
    );
}

// =============================================================================
// Shared bits
// =============================================================================
export function StatementPill({ status }) {
    const map = {
        pending: { tone: 'warning', label: 'Pending' },
        sent: { tone: 'neutral', label: 'Sent' },
        paid: { tone: 'success', label: 'Paid' },
        voided: { tone: 'warning', label: 'Voided' },
    };
    const c = map[status] || { tone: 'neutral', label: status };
    return <StatusPill status={c.tone}>{c.label}</StatusPill>;
}

function pence(p) {
    return `£${(Number(p || 0) / 100).toFixed(2)}`;
}

function fmtDate(d) {
    if (!d) return '—';
    const s = String(d).slice(0, 10);
    const dt = new Date(`${s}T00:00:00`);
    if (isNaN(dt.getTime())) return '—';
    return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function iso(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function presetRange(key) {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    if (key === 'this_month') {
        return { start: iso(new Date(t.getFullYear(), t.getMonth(), 1)), end: iso(new Date(t.getFullYear(), t.getMonth() + 1, 0)) };
    }
    if (key === 'last_week') {
        const day = t.getDay() || 7;
        const thisMon = new Date(t); thisMon.setDate(t.getDate() - (day - 1));
        const lastMon = new Date(thisMon); lastMon.setDate(thisMon.getDate() - 7);
        const lastSun = new Date(lastMon); lastSun.setDate(lastMon.getDate() + 6);
        return { start: iso(lastMon), end: iso(lastSun) };
    }
    if (key === 'this_year') {
        return { start: `${t.getFullYear()}-01-01`, end: `${t.getFullYear()}-12-31` };
    }
    // last_month
    return { start: iso(new Date(t.getFullYear(), t.getMonth() - 1, 1)), end: iso(new Date(t.getFullYear(), t.getMonth(), 0)) };
}
